import { Box, Link as MuiLink, Typography } from '@mui/material';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { neutral, palette } from '@ahora-patagonia/design-tokens';
import type { NotaLateral, PublicAd } from '@ahora-patagonia/contracts';
import { EspacioPublicitario } from '../ads/EspacioPublicitario.js';
import { intercaladosEnLista } from '../ads/cadencia.js';
import { formatearHora } from '../formato/fecha.js';
import { filasOcultas } from './recorte.js';

export interface TiraLateralProps {
  notas: NotaLateral[];
  /**
   * El rótulo, que el panel puede cambiar (`shell.ultimaHoraNombre` / `shell.masLeidasNombre`).
   * Tiene default porque las pantallas lo dibujan también mientras cargan, cuando todavía no hay
   * shell.
   */
  titulo?: string;
  /** El robapáginas (`SIDEBAR`), uno cada 5 notas. Sin campaña, la tira es solo notas. */
  ad: PublicAd | undefined;
  /** A dónde va el "Ver todo". Sin esto no se dibuja. */
  verTodo?: string;
  /**
   * Numera las filas en vez de fechar cada una. Es lo que distingue a un ranking de un orden
   * cronológico: en Más leídas el puesto es el dato, y la hora de publicación no dice nada.
   */
  numerada?: boolean;
}

/** Una fila de la tira: notas y avisos son hermanos, nunca uno adentro del otro (ver abajo). */
type Fila =
  | { tipo: 'nota'; clave: string; nota: NotaLateral; puesto: number }
  | { tipo: 'aviso'; clave: string };

/** `useLayoutEffect` en el navegador, `useEffect` en el server (React avisa si se usa en SSR). */
const useEfectoDeLayout = typeof window === 'undefined' ? useEffect : useLayoutEffect;

/** La tira aplanada: nota, nota, …, aviso, nota. La cadencia sale de `intercaladosEnLista`. */
function filasDeLaTira(notas: NotaLateral[], ad: PublicAd | undefined): Fila[] {
  const conAviso = ad ? intercaladosEnLista(notas.length) : new Set<number>();
  const filas: Fila[] = [];
  notas.forEach((nota, i) => {
    // El puesto se fija acá y no en el render: ahí el índice cuenta también los avisos, y el
    // ranking saltaría de 5 a 7 en cuanto hubiera una campaña.
    filas.push({ tipo: 'nota', clave: nota.id, nota, puesto: i + 1 });
    if (conAviso.has(i)) filas.push({ tipo: 'aviso', clave: `aviso-${nota.id}` });
  });
  return filas;
}

/**
 * La columna lateral de Zonda (≥1200): **una sola tira de titulares con un 300×600 cada
 * 5 notas**, que llega hasta el pie del cuerpo. El mejor inventario de escritorio del
 * proyecto, y va igual en todas las pantallas con lateral.
 *
 * Casi siempre lleva Última hora. La excepción es `/ultima-hora`, donde eso sería repetir el
 * cuerpo fila por fila: ahí lleva el ranking, numerado (ver `numerada`). Quién va en cada pantalla
 * lo decide la vista, no este componente.
 *
 * El corte a la altura del cuerpo es CSS: el wrapper toma el alto de la celda del grid (que
 * estira a la altura de la columna principal) y el inner es `absolute` + `overflow: hidden`.
 * Se piden ~30 notas y sobra; lo que no entra, no se ve.
 *
 * El `height: 100%` del wrapper no es decorativo: su único hijo está fuera de flujo, así que
 * sin alto explícito la caja colapsa a 0 y el `overflow: hidden` recorta la tira completa.
 *
 * Pero ese recorte cae en un píxel cualquiera, no en el borde de una fila: sin ayuda, la
 * última nota visible queda partida al medio (la hora y la mitad del titular). Así que la
 * **única medición de JS del sitio** apaga las filas que no entran enteras — la regla, con su
 * asimetría entre nota y aviso, está aparte y testeada en `recorte.ts`.
 *
 * Tres detalles que hacen que la medición sea correcta y no una foto de un instante:
 *
 *  - Las filas son **hermanas**: el aviso no va adentro del `<Box>` de la nota. Si fueran un
 *    solo bloque, "la fila que no entra" mezclaría nota y robapáginas y no habría nada que
 *    decidir.
 *  - Se re-mide con un `ResizeObserver` sobre el wrapper — redimensionar la ventana mueve el
 *    alto del cuerpo y con él el punto de corte — y otra vez con `document.fonts.ready`,
 *    porque la tipografía entra con `font-display: swap` y al llegar re-arma los renglones de
 *    los titulares. Observar el wrapper no puede realimentarse: su alto lo fija el grid y el
 *    contenido que tocamos vive en el inner, que está fuera de flujo.
 *  - Se oculta con `display: none`, no con `visibility`: un robapáginas que no entra tiene que
 *    devolver sus 600 px a las notas de abajo, no dejar un hueco blanco al pie de la columna.
 */
export function TiraLateral({
  notas,
  titulo = 'Última hora',
  ad,
  verTodo,
  numerada = false,
}: TiraLateralProps) {
  const wrapper = useRef<HTMLDivElement>(null);
  const inner = useRef<HTMLDivElement>(null);
  const filas = useMemo(() => filasDeLaTira(notas, ad), [notas, ad]);

  const medir = useCallback(() => {
    const contenedor = inner.current;
    if (!contenedor) return;
    const elementos = Array.from(contenedor.querySelectorAll<HTMLElement>('[data-fila]'));
    const [primeraFila] = elementos;
    if (!primeraFila) return;

    // Siempre se mide en limpio: si no, el alto de una fila apagada en la medición anterior
    // sería 0 y la tira se iría achicando sola.
    elementos.forEach((elemento) => {
      elemento.style.display = '';
    });

    // El encabezado "Última hora" también come alto; lo que sobra para las filas es lo que
    // hay entre el techo de la primera y el pie del contenedor. Rects y no `offsetHeight`:
    // 30 redondeos a entero se suman hasta la media fila de error.
    const caja = contenedor.getBoundingClientRect();
    const disponible =
      caja.top + contenedor.clientHeight - primeraFila.getBoundingClientRect().top;

    const ocultas = filasOcultas(
      elementos.map((elemento) => ({
        alto: elemento.getBoundingClientRect().height,
        esAviso: elemento.dataset['fila'] === 'aviso',
      })),
      disponible,
    );
    elementos.forEach((elemento, i) => {
      elemento.style.display = ocultas.has(i) ? 'none' : '';
    });
  }, []);

  useEfectoDeLayout(() => {
    medir();
    const caja = wrapper.current;
    if (!caja || typeof ResizeObserver === 'undefined') return;
    const observador = new ResizeObserver(medir);
    observador.observe(caja);
    return () => observador.disconnect();
  }, [medir, filas]);

  useEffect(() => {
    // `fonts` no existe en jsdom ni en navegadores viejos: la tira ya está recortada sin esto.
    void document.fonts?.ready.then(medir);
  }, [medir, filas]);

  // Sin notas no hay columna: ni el rótulo, ni la línea que lo subraya. Va acá abajo y no arriba
  // de los hooks a propósito (no se puede retornar antes de un hook), y hace falta además del
  // filtro del servidor: la home le pasa `[]` cuando el bloque no viene, así que sin esto el
  // encabezado se dibujaría igual sobre una tira vacía.
  //
  // No se pierde el robapáginas: la tira intercala un aviso *entre* notas, así que sin notas
  // `filasDeLaTira` ya devolvía `[]` y el `SIDEBAR` no se mostraba tampoco.
  if (notas.length === 0) return null;

  return (
    <Box ref={wrapper} sx={{ position: 'relative', height: '100%', minWidth: 300, width: 300 }}>
      <Box ref={inner} sx={{ position: 'absolute', inset: 0, overflow: 'hidden' }}>
        {/* El "Ver todo" va en el encabezado y no al pie de la tira porque la tira se recorta por
            alto: al pie sería lo primero en desaparecer, justo cuando hay tantas notas que más
            falta hace. Acá no molesta al recorte — `medir` cuenta desde el techo de la primera
            fila, así que el encabezado puede crecer sin mover el punto de corte. */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'baseline',
            justifyContent: 'space-between',
            gap: 1,
            borderBottom: `2px solid ${palette.ink}`,
            pb: 0.5,
          }}
        >
          <Typography variant="tituloBloque" component="h2" sx={{ minWidth: 0 }}>
            {titulo}
          </Typography>
          {verTodo && (
            <MuiLink
              component={RouterLink}
              to={verTodo}
              sx={{
                flexShrink: 0,
                fontWeight: 800,
                fontSize: 13,
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
              }}
            >
              Ver todo
            </MuiLink>
          )}
        </Box>
        {filas.map((fila, i) =>
          fila.tipo === 'nota' ? (
            <Box
              key={fila.clave}
              data-fila="nota"
              sx={{ py: 1.25, ...(i > 0 ? { borderTop: `1px solid ${neutral[300]}` } : {}) }}
            >
              {numerada ? (
                <Typography
                  variant="volanta"
                  component="span"
                  sx={{ color: palette.ink, fontWeight: 800 }}
                >
                  {fila.puesto}
                </Typography>
              ) : (
                <Typography variant="volanta" component="time" sx={{ color: neutral[600] }}>
                  {formatearHora(fila.nota.publishedAt)}
                </Typography>
              )}
              <MuiLink component={RouterLink} to={fila.nota.path} sx={{ display: 'block' }}>
                <Typography component="h3" sx={{ fontWeight: 800, fontSize: 15, lineHeight: 1.3 }}>
                  {fila.nota.title}
                </Typography>
              </MuiLink>
            </Box>
          ) : (
            <Box key={fila.clave} data-fila="aviso" sx={{ py: 1 }}>
              <EspacioPublicitario ubicacion="SIDEBAR" ad={ad} />
            </Box>
          ),
        )}
      </Box>
    </Box>
  );
}
